import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  InputAdornment,
  IconButton,
  Typography,
} from "../../../common/utils/mui-imports";

import { RejectReopenPopupProps } from "./RejectReopenPopup.types";
import { ClearIcon } from "@mui/x-date-pickers";
import { EnumTextBoxType } from "../../common/HPRTextBox/HPRTextBox.enum";
import HPRTextBoxAutoResize from "../../common/HPRTextBox/HPRTextBoxAutoResize";
import { CloseCircle, ShieldTick } from "iconsax-react";
import { useAppDispatch } from "../../../core/hooks";
import { openPopup } from "../Popup.reducer";
import { EnumPopupType } from "../Popup.enum";
import { PopupProps } from "../PopupProps.types";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const RejectReopenPopup: React.FC<RejectReopenPopupProps> = ({
  Open,
  HeaderText,
  BodyText,
  OkButtonText = "Confirm",
  CancelButtonText = "Discard",
  CloseBtnClick,
  OkBtnClick,
  CancelBtnClick,
}) => {
  const [rejectReason, setRejectReason] = useState<string>("");
  const [validator, setValidator] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const dispatch = useAppDispatch();

  const resetState = () => {
    setRejectReason("");
    setErrorMessage("");
  };

  const handleClose = () => {
    resetState();
    CloseBtnClick?.();
  };

  const handleOk = () => {
    if (!rejectReason.trim()) {
      setErrorMessage("Please enter a reason for Re-open.");
      return;
    }
    OkBtnClick?.(rejectReason);
    resetState();
  };

  const handleCancel = () => {
    if (rejectReason.trim()) {
      dispatch(
        openPopup({
          Open: true,
          BodyText: "Do you want to discard the changes?",
          HeaderText: "Confirmation",
          PopupType: EnumPopupType.YesNoConfirmation,
          YesBtnClick: () => {
            resetState();
            CancelBtnClick?.();
          },
          NoBtnClick: () => {},
          DisableBackdropClose: true,
        } as PopupProps)
      );
    } else {
      resetState();
      CancelBtnClick?.();
    }
  };

  const onChangeRejectReason = (_fieldName: string, value: string) => {
    setRejectReason(value);
  };

  const clearReason = () => {
    setRejectReason("");
  };

  return (
    <Modal
      className="modal-outter modal-md"
      open={Open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="modal-header">
          <h1>{HeaderText}</h1>
        </div>

        <div className="modal-content mt-10" style={{ position: "relative" }}>
          {BodyText && <p dangerouslySetInnerHTML={{ __html: BodyText }} />}

          <HPRTextBoxAutoResize
            Id="reasonForRejection"
            Name="reasonForRejection"
            Label="Reason for Re-open"
            Required
            TextLength={500}
            InputProps={{ maxLength: 500 }}
            ComponentProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear text"
                    edge="end"
                    onClick={clearReason}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            ShowTextCounter
            Value={rejectReason ?? ""}
            Type={EnumTextBoxType.Text}
            Disabled={false}
            onTextBoxChange={onChangeRejectReason}
            Validator={validator}
            Rules="required"
            IsEnableValidator
            ShowValidMsg
            MinRows={3}
          />

          <Typography
            variant="body2"
            className="ml-10"
            style={{
              color: "red",
              fontSize: "15px",
              position: "absolute",
              bottom: "3px",
              left: "15px",
            }}
          >
            {errorMessage}
          </Typography>
        </div>

        <div className="modal-button-bar">
          <Button
            className="secondary-btn-small"
            variant="outlined"
            startIcon={<CloseCircle variant="Outline" />}
            onClick={handleCancel}
          >
            {CancelButtonText}
          </Button>

          <Button
            className="primary-btn-small"
            variant="contained"
            startIcon={<ShieldTick variant="Outline" />}
            onClick={handleOk}
          >
            {OkButtonText}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default RejectReopenPopup;
